import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Screen } from '../components/Layout'
import { Button, Card, ErrorBanner } from '../components/ui'
import { config } from '../lib/config'
import { longDate } from '../lib/format'
import { useMe } from '../lib/queries'
import { useSession } from '../session'

/**
 * The account screen: who is signed in, and the way out (requirement 1).
 *
 * Reached from the button in every screen's top-right corner. It also holds the
 * settings that belong to the account rather than to a month -- 分類管理 is one,
 * and the reports screen was a strange place for the only door to it.
 */
export function ProfilePage() {
  const me = useMe()
  const { session, signOut } = useSession()
  const [confirming, setConfirming] = useState(false)

  const profile = me.data?.profile
  // Fall back to the id token's own claims while `/me` is in flight. The name and
  // the email are in the token the browser already holds, so this screen has
  // something true to show at once instead of a spinner over an empty card.
  const name = profile?.displayName ?? session?.displayName
  const email = profile?.email ?? session?.email

  return (
    <Screen title="我的帳號">
      <div className="space-y-4">
        {me.error ? <ErrorBanner error={me.error} onRetry={() => void me.refetch()} /> : null}

        <Card className="flex items-center gap-4 p-4">
          <span
            aria-hidden
            className="flex size-12 shrink-0 items-center justify-center rounded-full bg-ink text-xl font-semibold text-accent ring-1 ring-ink-line"
          >
            {(name ?? email ?? '?').trim().charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-lg">{name ?? '—'}</p>
            <p className="truncate text-sm text-muted">{email ?? '—'}</p>
          </div>
        </Card>

        <Card className="divide-y divide-ink-line/40">
          <Row label="基準貨幣" value={profile?.baseCurrency ?? '—'} />
          {/* `createdAt` is a full timestamp; the day is the only part that means
              anything here. */}
          <Row label="加入時間" value={profile ? longDate(profile.createdAt.slice(0, 10)) : '—'} />
        </Card>

        <Card>
          <Link to="/categories" className="flex items-center justify-between px-4 py-3">
            <span>分類管理</span>
            <span aria-hidden className="text-muted">
              ›
            </span>
          </Link>
        </Card>

        <Card className="space-y-3 p-4">
          {confirming ? (
            <>
              <p className="text-sm">
                登出後要再用 Google 登入才能看到帳本。已經記下的支出和分帳都會保留。
              </p>
              <div className="flex gap-2">
                <Button variant="danger" className="flex-1" onClick={signOut}>
                  確定登出
                </Button>
                <Button variant="secondary" className="flex-1" onClick={() => setConfirming(false)}>
                  取消
                </Button>
              </div>
            </>
          ) : (
            <Button variant="danger" className="w-full" onClick={() => setConfirming(true)}>
              登出
            </Button>
          )}
          {/* Dev impersonation is read from the environment on every load, so
              logging out of it lands back on the same account one redirect later.
              Saying so beats debugging a button that looks broken. */}
          {import.meta.env.DEV && config.devUser ? (
            <p className="text-xs text-muted">
              本機用 VITE_DEV_USER_ID 假冒登入，登出後會立刻被認回來。
            </p>
          ) : null}
        </Card>
      </div>
    </Screen>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between px-4 py-3">
      <span className="text-sm text-muted">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  )
}
